import { throwApiError } from '@/shared/utils/apiResponse';
import { apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import { Conversa, Mensagem, NegociacaoAttachmentUploadResult } from '../types/messaging.types';

const normalizeListResponse = <TItem>(body: unknown): { items: TItem[]; total: number } => {
  const payload = (body as { data?: unknown })?.data ?? body;

  if (Array.isArray(payload)) {
    return { items: payload as TItem[], total: payload.length };
  }

  const source = payload as { items?: unknown; total?: unknown } | null;
  const items = Array.isArray(source?.items) ? (source.items as TItem[]) : [];
  const total = typeof source?.total === 'number' ? source.total : items.length;
  return { items, total };
};

export const messagingService = {
  getConversas: async (params: {
    empresaId: string;
    escritorioId?: string;
    entityType?: string;
    entityId?: string;
    status?: Conversa['status'];
    mode?: Conversa['mode'];
    updatedSince?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: Conversa[]; total: number }> => {
    const response = await apiGet(
      '/conversas',
      params as Record<string, string | number | boolean>,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar conversas');
    }
    const body = await response.json();
    return normalizeListResponse<Conversa>(body);
  },

  getMensagens: async (params: {
    conversaId: string;
    updatedSince?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: Mensagem[]; total: number }> => {
    const response = await apiGet(
      '/mensagens',
      params as Record<string, string | number | boolean>,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar mensagens');
    }
    const body = await response.json();
    return normalizeListResponse<Mensagem>(body);
  },

  catchUpConversas: async (updatedSince: string): Promise<{ items: Conversa[]; total: number }> => {
    const response = await apiGet('/conversas/realtime/catch-up', { updatedSince });
    if (!response.ok) {
      await throwApiError(response, 'Erro no catch-up de conversas');
    }
    const body = await response.json();
    return normalizeListResponse<Conversa>(body);
  },

  catchUpMensagens: async (
    conversaId: string,
    updatedSince: string,
  ): Promise<{ items: Mensagem[]; total: number }> => {
    const response = await apiGet('/mensagens/realtime/catch-up', { conversaId, updatedSince });
    if (!response.ok) {
      await throwApiError(response, 'Erro no catch-up de mensagens');
    }
    const body = await response.json();
    return normalizeListResponse<Mensagem>(body);
  },

  updateConversa: async (
    conversaId: string,
    patch: { mode?: Conversa['mode']; status?: Conversa['status'] },
  ): Promise<Conversa> => {
    const response = await apiPut(`/conversas/${conversaId}`, patch);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao atualizar conversa');
    }
    const body = await response.json();
    const payload = (body.data || body) as Conversa;
    return payload;
  },

  createMensagem: async (input: {
    empresaId: string;
    escritorioId: string;
    conversaId: string;
    direction: Mensagem['direction'];
    canal: Mensagem['canal'];
    tipo: Mensagem['tipo'];
    textBody?: string;
    mediaUrl?: string;
    providerMessageId?: string;
    status?: Mensagem['status'];
    metadata?: Record<string, unknown>;
  }): Promise<Mensagem> => {
    const response = await apiPost('/mensagens', input);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao enviar mensagem');
    }
    const body = await response.json();
    const payload = (body.data || body) as Mensagem;
    return payload;
  },

  sendNegotiacaoMessage: async (input: {
    phone: string;
    message: string;
    mediaUrl?: string;
    type: 'text' | 'audio' | 'image' | 'document';
    metadata: {
      caseId: string;
      empresaId?: string;
      escritorioId?: string;
      filename?: string;
    };
  }): Promise<{ success: boolean; messageId?: string }> => {
    const response = await apiPost('/negociacao/messages/send', input);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao enviar mensagem');
    }

    const body = (await response.json()) as unknown;
    return body as { success: boolean; messageId?: string };
  },

  uploadNegociacaoAttachment: async (
    caseId: string,
    file: File,
  ): Promise<NegociacaoAttachmentUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiPost(`/negociacao/casos/${caseId}/attachments`, formData);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao enviar anexo');
    }
    const body = await response.json();
    return (body.data || body) as NegociacaoAttachmentUploadResult;
  },

  updateMensagem: async (
    mensagemId: string,
    patch: { status?: Mensagem['status']; providerMessageId?: string; errorMessage?: string },
  ): Promise<Mensagem> => {
    const response = await apiPut(`/mensagens/${mensagemId}`, patch);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao atualizar mensagem');
    }
    const body = await response.json();
    const payload = (body.data || body) as Mensagem;
    return payload;
  },
};
